'use client';

import { IT_INT } from '@slims/shared/constants';
import {
	type BollettaExtractedFields,
	type BollettaSavedResult,
	BollettaUploadCard,
} from '@slims/ui/molecules';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Meter } from '@/hooks/use-meters';
import { api, apiFetch } from '@/lib/utils/api';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

interface BollettaUploadSectionProps {
	meter: Meter;
}

interface StorageUploadResponse {
	publicUrl: string;
	error?: string;
}

interface ExtractBollettaResponse {
	fields: BollettaExtractedFields;
	confidence: 'high' | 'medium' | 'low';
}

/**
 * Wiring di `BollettaUploadCard`: carica il file su storage, lo fa leggere
 * dall'AI (`/api/documents/extract-bolletta`), e alla conferma dell'operatore
 * scrive il totale sul misuratore (`/api/meters/:id/bolletta`) — due chiamate
 * separate apposta, perché l'estrazione va rivista prima di diventare un dato
 * sul misuratore.
 */
export function BollettaUploadSection({ meter }: BollettaUploadSectionProps) {
	const queryClient = useQueryClient();
	const orgId = useAdminScopeStore((s) => s.configId);

	const [extracting, setExtracting] = useState(false);
	const [saving, setSaving] = useState(false);
	const [extracted, setExtracted] = useState<BollettaExtractedFields | null>(null);
	const [confidence, setConfidence] = useState<'high' | 'medium' | 'low' | null>(null);
	const [saved, setSaved] = useState<BollettaSavedResult | null>(null);
	const [error, setError] = useState<string | null>(null);

	// L'ultima bolletta già registrata: senza, ricaricando la pagina la card
	// tornava al form vuoto, come se la bolletta non ci fosse (GitHub #266).
	useEffect(() => {
		if (!orgId) return;
		let cancelled = false;
		api
			.get<BollettaSavedResult | null>(`/api/meters/${meter.id}/bolletta?orgId=${orgId}`)
			.then((ultima) => {
				if (!cancelled && ultima?.from && ultima.to) setSaved((s) => s ?? ultima);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [meter.id, orgId]);

	async function handleExtract(file: File) {
		if (!orgId) {
			setError('Nessuna configurazione selezionata');
			return;
		}
		setExtracting(true);
		try {
			const safeName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
			const form = new FormData();
			form.append('file', file);
			form.append('path', `documents/bolletta/${meter.id}/${Date.now()}_${safeName}`);
			const uploadRes = await apiFetch(`/api/storage/upload?orgId=${orgId}`, {
				method: 'POST',
				body: form,
			});
			const uploadJson = (await uploadRes.json()) as StorageUploadResponse;
			if (!uploadRes.ok) throw new Error(uploadJson.error ?? 'Errore caricamento file');

			const res = await api.post<ExtractBollettaResponse>(
				`/api/documents/extract-bolletta?orgId=${orgId}`,
				{ documentUrl: uploadJson.publicUrl, documentNome: file.name },
			);
			setExtracted(res.fields);
			setConfidence(res.confidence);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Estrazione fallita');
		} finally {
			setExtracting(false);
		}
	}

	async function handleConfirm(fields: {
		periodoDa: string;
		periodoA: string;
		energiaKwh: number;
	}) {
		if (!orgId) return;
		setSaving(true);
		try {
			const res = await api.post<BollettaSavedResult>(
				`/api/meters/${meter.id}/bolletta?orgId=${orgId}`,
				fields,
			);
			setSaved(res);
			void queryClient.invalidateQueries({ queryKey: ['meter-hourly', orgId, meter.id] });
			setExtracted(null);
			toast.success(`Bolletta registrata · ${res.days.toLocaleString('it-IT', IT_INT)} giorni`);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Registrazione fallita');
		} finally {
			setSaving(false);
		}
	}

	function handleReset() {
		setExtracted(null);
		setSaved(null);
		setConfidence(null);
		setError(null);
	}

	return (
		<BollettaUploadCard
			extracting={extracting}
			saving={saving}
			extracted={extracted}
			confidence={confidence}
			saved={saved}
			error={error}
			onExtract={(file) => {
				void handleExtract(file);
			}}
			onConfirm={(fields) => {
				void handleConfirm(fields);
			}}
			onReset={handleReset}
		/>
	);
}
